/**
 * Listes déroulantes Studios et Coachs du planning v2 (screens/Planning/FilterSelect.tsx) : option « Tous les studios »
 * / « Tous les coachs » en tête du menu et messages du lecteur d'écran en français. Fonctions pures.
 *
 * Retours de Daphné du 12/10 : « Il manque la possibilité, dans la liste déroulante des studios, de pouvoir rebasculer
 * sur la vue d'ensemble (Tous les studios). Idem pour les coachs. » L'option vide la sélection et ferme le menu en un
 * clic ; elle est cochée quand rien n'est choisi. Elle n'entre jamais dans la valeur de la liste (`value` reste la
 * liste des clés « site:id » choisies).
 */
import type { AriaLiveMessages, GroupBase } from 'chakra-react-select';
import type { FilterOption, FilterOptionGroup } from './filters';

/** Valeur de l'option « Tous » : ne peut pas être une clé « site:id ». */
export const ALL_OPTION_VALUE = '__all__';

export const allOption = (label: string): FilterOption => ({ value: ALL_OPTION_VALUE, label, siteId: '' });

export const isAllOption = (option: FilterOption | null | undefined): boolean => option?.value === ALL_OPTION_VALUE;

/**
 * Options du menu : « Tous » d'abord, puis les options (vue simple) ou les groupes par marque (vue multi-marques
 * groupée) ; react-select accepte une option seule au premier niveau, à côté des groupes.
 *
 * Pendant une recherche au clavier (`searching` : saisie du champ non vide), « Tous » n'est pas proposée : la liste ne
 * montre que les studios / coachs trouvés, filtrés par react-select comme avant. Relecture du 12/10 : le libellé
 * « Tous les studios » répondait à « s », « o », « les »… et restait en tête, survolé ; Entrée vidait alors la
 * sélection et fermait la liste au lieu de cocher le premier studio trouvé (« s » : La Défense) ; « Tous les coachs »
 * restait seule pour « ch », sans aucun coach trouvé, annoncée « 1 résultat ». Sans filterOption ni createFilter :
 * rien de plus n'est importé de react-select, le bundle de la v1 reste identique.
 */
export function menuOptions(
  groups: FilterOptionGroup[],
  grouped: boolean,
  allLabel: string,
  searching = false,
): Array<FilterOption | FilterOptionGroup> {
  const items: Array<FilterOption | FilterOptionGroup> = grouped ? groups : groups.flatMap((group) => group.options);
  return searching ? items : [allOption(allLabel), ...items];
}

/** Option cochée : « Tous » quand rien n'est choisi, une autre option quand elle est choisie. */
export function isOptionChecked(option: FilterOption, selected: readonly FilterOption[]): boolean {
  return isAllOption(option) ? selected.length === 0 : selected.some((s) => s.value === option.value);
}

/**
 * Valeur après un choix dans le menu (onChange de react-select, sélection multiple) : « Tous » vide la sélection et
 * ferme le menu ; un autre choix garde le menu ouvert (plusieurs studios ou coachs à cocher), comme avant.
 * `changed` : faux si « Tous » est choisi alors que rien ne l'était (rien à transmettre).
 */
export function valueAfterChange(
  next: readonly FilterOption[],
  option: FilterOption | null | undefined,
  previous: readonly string[],
): { value: string[]; close: boolean; changed: boolean } {
  if (isAllOption(option)) return { value: [], close: true, changed: previous.length > 0 };
  const value = next.filter((o) => !isAllOption(o)).map((o) => o.value);
  return { value, close: false, changed: true };
}

const plural = (count: number, one: string, many: string) => `${count} ${count > 1 ? many : one}`;

/**
 * Messages de la zone « aria-live » de react-select, en français (ceux de la bibliothèque sont en anglais : « option
 * Louvre, selected. »). react-select 5.8 (celui de chakra-react-select) donne au menu le rôle listbox
 * (aria-multiselectable) et à chaque option le rôle option avec aria-selected, suivie par aria-activedescendant ; sur
 * les appareils Apple (VoiceOver), sans aria-selected ni aria-activedescendant, l'option survolée au clavier est
 * annoncée par la zone « aria-live » : mêmes règles ici (onFocus seulement sur Apple, consigne du champ seulement à
 * son premier focus). `label` : nom de la liste (« Studios »).
 */
export function frenchAriaLiveMessages(
  label: string,
): AriaLiveMessages<FilterOption, true, GroupBase<FilterOption>> {
  return {
    guidance: ({ context, isSearchable, isInitialFocus }) => {
      if (context === 'menu') {
        return 'Flèches haut et bas pour parcourir la liste, Entrée pour cocher ou décocher, Échap pour la fermer.';
      }
      if (context === 'input' && isInitialFocus) {
        return `${label} : flèche bas pour ouvrir la liste${isSearchable ? ', ou saisissez un nom pour la filtrer' : ''}.`;
      }
      return '';
    },
    onChange: (props) => {
      const { action, label: optionLabel, labels } = props;
      const option = 'option' in props ? props.option : undefined;
      if (action === 'initial-input-focus') return labels.length ? `Choix : ${labels.join(', ')}.` : '';
      if (isAllOption(option)) return `${optionLabel} : aucun filtre.`;
      if (action === 'select-option') return `${optionLabel} coché.`;
      if (action === 'deselect-option' || action === 'remove-value' || action === 'pop-value') {
        return `${optionLabel} décoché.`;
      }
      if (action === 'clear') return 'Sélection vidée.';
      return '';
    },
    onFocus: ({ context, focused, label: optionLabel, options, selectValue, isAppleDevice }) => {
      if (context !== 'menu' || !isAppleDevice) return '';
      const index = options.indexOf(focused);
      const position = index >= 0 ? `, ${index + 1} sur ${options.length}` : '';
      return `${optionLabel}${isOptionChecked(focused, selectValue) ? ', coché' : ''}${position}.`;
    },
    onFilter: ({ inputValue, resultsMessage }) =>
      inputValue ? `${resultsMessage} pour « ${inputValue} ».` : `${resultsMessage}.`,
  };
}

/** Nombre de résultats du menu (screenReaderStatus de react-select). */
export const resultsStatus = ({ count }: { count: number }) =>
  count === 0 ? 'Aucun résultat' : plural(count, 'résultat', 'résultats');
